import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PasswordInput } from "@/components/ui/password-input";
import { adminExtend, signIn } from "@/lib/auth-client";

type SignupSearchParams = {
  token?: string;
};

export const Route = createFileRoute("/auth/signup")({
  component: SignupPage,
  validateSearch: (search: Record<string, unknown>): SignupSearchParams => {
    return {
      token: search.token as string | undefined,
    };
  },
});

const signupSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .max(128, "Password must be less than 128 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type SignupFormValues = z.infer<typeof signupSchema>;

function SignupPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  // Check for missing token
  useEffect(() => {
    if (!token) {
      setError(
        "Invalid invitation link. Please check your email for the correct link or contact your administrator.",
      );
    }
  }, [token]);

  async function onSubmit(data: SignupFormValues) {
    if (!token) {
      setError("Missing invitation token");
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const result = await adminExtend.acceptInvitation({
        token,
        password: data.password,
      });

      if (result.error) {
        // Handle specific error codes
        const errorData = result.error as {
          message?: string;
          code?: string;
        };
        if (errorData.code === "TOKEN_EXPIRED") {
          setError(
            "This invitation has expired. Please contact your administrator for a new invitation.",
          );
        } else if (errorData.code === "USER_EXISTS") {
          setError(
            "An account with this email already exists. Please sign in instead.",
          );
        } else {
          setError(
            errorData.message ||
              "Failed to create account. Please try again or contact your administrator.",
          );
        }
        setIsSubmitting(false);
        return;
      }

      if (result.data?.success && result.data?.email) {
        setIsSuccess(true);
        setIsSigningIn(true);

        // Automatically sign in
        const signInResult = await signIn.email({
          email: result.data.email,
          password: data.password,
          callbackURL: "/dashboard",
        });

        if (signInResult.error) {
          // Sign-in failed, redirect to login page
          setIsSigningIn(false);
          setError(
            "Account created but sign-in failed. Redirecting to login...",
          );
          setTimeout(() => {
            navigate({
              to: "/auth/signin",
              search: { email: result.data?.email },
            });
          }, 2000);
          return;
        }

        // Success - callback URL will handle redirect
      }
    } catch (err) {
      setError(
        (err as Error)?.message ||
          "An unexpected error occurred. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  // Success state - signing in
  if (isSuccess) {
    return (
      <div className="bg-topography flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
        <Card className="w-full max-w-sm p-6">
          <div className="flex flex-col items-center gap-4 text-center">
            {isSigningIn ? (
              <>
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <h2 className="text-xl font-semibold">
                  Account Created Successfully!
                </h2>
                <p className="text-muted-foreground text-sm">
                  Signing you in...
                </p>
              </>
            ) : (
              <>
                <CheckCircle2 className="h-10 w-10 text-green-500" />
                <h2 className="text-xl font-semibold">Welcome!</h2>
                <p className="text-muted-foreground text-sm">
                  Redirecting to dashboard...
                </p>
              </>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // Error state - no token
  if (!token) {
    return (
      <div className="bg-topography flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
        <Card className="w-full max-w-md p-8">
          <div className="flex flex-col items-center gap-4 text-center">
            <AlertCircle className="h-12 w-12 text-red-500" />
            <h2 className="text-xl font-semibold">Invalid Invitation Link</h2>
            <p className="text-muted-foreground text-sm">
              This invitation link appears to be invalid or incomplete. Please
              check your email for the correct link.
            </p>
            <div className="mt-4 p-4 bg-muted rounded-lg text-sm text-left w-full">
              <p className="font-medium mb-2">Need help?</p>
              <p className="text-muted-foreground">
                Contact your administrator to request a new invitation.
              </p>
            </div>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate({ to: "/auth/signin" })}
            >
              Go to Sign In
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Normal form state
  return (
    <div className="bg-topography flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-3xl">
        <div className="flex flex-col gap-10 py-12">
          <Card className="p-0 my-8">
            <CardContent className="grid p-0 md:grid-cols-2">
              <div className="relative min-h-[500px] flex flex-col justify-center">
                <Form {...form}>
                  <form
                    className="p-10 md:p-14"
                    onSubmit={form.handleSubmit(onSubmit)}
                    noValidate
                  >
                    <div className="flex flex-col gap-8">
                      <div className="flex flex-col items-center text-center">
                        <img
                          src="/cte-logo.png"
                          alt="CTE Logo"
                          className="mb-4 h-12 w-auto"
                        />
                        <h1 className="text-2xl font-bold">
                          Complete Your Account
                        </h1>
                        <p className="text-muted-foreground text-balance mt-2">
                          You've been invited to join the platform. Set your
                          password to complete registration.
                        </p>
                      </div>

                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem className="relative pb-5">
                            <FormLabel>Password</FormLabel>
                            <FormControl>
                              <PasswordInput
                                {...field}
                                id="password"
                                name="password"
                                placeholder="Enter your password"
                                required
                                autoComplete="new-password"
                              />
                            </FormControl>
                            <FormMessage className="absolute left-0 bottom-0" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="confirmPassword"
                        render={({ field }) => (
                          <FormItem className="relative pb-5">
                            <FormLabel>Confirm Password</FormLabel>
                            <FormControl>
                              <PasswordInput
                                {...field}
                                id="confirmPassword"
                                name="confirmPassword"
                                placeholder="Confirm your password"
                                required
                                autoComplete="new-password"
                              />
                            </FormControl>
                            <FormMessage className="absolute left-0 bottom-0" />
                          </FormItem>
                        )}
                      />

                      <div className="relative">
                        <Button
                          type="submit"
                          className="w-full"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Creating Account...
                            </>
                          ) : (
                            "Create Account"
                          )}
                        </Button>

                        {error && (
                          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                              <div>
                                <p>{error}</p>
                                <p className="mt-2 text-xs text-red-600">
                                  If this problem persists, please contact your
                                  administrator.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <p className="text-xs text-muted-foreground text-center">
                        Already have an account?{" "}
                        <button
                          type="button"
                          onClick={() => navigate({ to: "/auth/signin" })}
                          className="text-primary hover:underline underline-offset-4"
                        >
                          Sign in
                        </button>
                      </p>
                    </div>
                  </form>
                </Form>
              </div>
              <div className="bg-muted relative hidden md:block">
                <img
                  src="/login-bg.jpg"
                  alt="Substation at night"
                  className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
                  loading="lazy"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
